import {
  gql,
  OperationVariables,
  QueryHookOptions,
  useQuery,
} from '@apollo/client';
import React from 'react';

import { TenantDomain } from './types';

export type IGetHumioTenentType = {
  getHumioDashboards: TenantDomain;
};

export interface IGetHumioTenentVariables extends OperationVariables {
  humioSessionToken: string;
}

export const TENENT_DASHBOARDS = gql`
  query getHumioDashboards($humioSessionToken: String!) {
    getHumioDashboards(humioSessionToken: $humioSessionToken) {
      searchDomain {
        dashboards {
          name
          id
        }
      }
    }
  }
`;

/**
 * Hook to fetch Humio dashboards for the authenticated tenant
 * Uses Apollo Client's useQuery with caching for optimal performance
 * @param {QueryHookOptions} options - Query options including skip, variables, callbacks
 * @returns {QueryResult} Apollo query result with data, loading, and error states
 */
export const useGetHumioDashboards = (
  options?: QueryHookOptions<IGetHumioTenentType, IGetHumioTenentVariables>,
) => {
  const result = useQuery<IGetHumioTenentType, IGetHumioTenentVariables>(
    TENENT_DASHBOARDS,
    {
      ...options,
      // Respect caller's fetchPolicy; default to cache-first to reduce early network noise
      fetchPolicy: options?.fetchPolicy ?? 'cache-first',
      nextFetchPolicy: options?.nextFetchPolicy ?? 'cache-first',
      notifyOnNetworkStatusChange:
        options?.notifyOnNetworkStatusChange ?? false,
      onError: options?.onError,
    },
  );

  // Use useEffect for onCompleted logic
  React.useEffect(() => {
    if (result.data && options?.onCompleted) {
      options.onCompleted(result.data);
    }
  }, [result.data, options?.onCompleted]);

  return result;
};
